type AudienceOptions = {
	country?: { [key: string]: string };
	department?: { [key: string]: string };
	employment_type?: { [key: string]: string };
	location?: { [key: string]: string };
	role?: { [key: string]: string };
};

export enum CatalogState {
	Active = 'active',
	Paused = 'paused',
	Shutoff = 'shutoff',
}

type ChatIntegrations = {
	has_slack_integration: boolean;
	has_ms_teams_integration: boolean;
};

export enum ProductTier {
	TierFree = 'tier_free', // free plan
	TierThree = 'tier_three', // Organization plan
	TierAlpha = 'tier_alpha', // Team plan
}

export enum UserMode {
	Benefactor = 'benefactor',
	Bot = 'bot',
	Normal = 'normal',
	Observer = 'observer',
	Receiver = 'receiver',
}

export enum PromotedRewardsSetting {
	BonuslyPromoted = 'bonusly_promoted',
	CompanyPromoted = 'company_promoted',
	Disabled = 'disabled',
}

export interface CompanySignUp {
	user_name: string | null;
	job_title: string | null;
	company_size: number;
	state: string;
}
export interface Company {
	id: string;
	active_user_count?: number;
	advanced_give_amounts_url: string | null;
	allow_auto_join: boolean;
	audience_options: AudienceOptions;
	auto_approve_max: number;
	automatic_user_management_enabled: boolean;
	axomo_subdomain?: string;
	banned_hashtags: string[];
	bizy_enabled: boolean;
	// Still supplied by CompanyBlueprint; retained for the blueprint/super-admin
	// contract. No frontend reader since BIZY-331 moved early-access eligibility
	// gating to the promo system (backend ListActivePromosForViewer).
	bizy_early_access_eligible: boolean;
	bonus_privacy: boolean;
	feature_states?: Record<string, boolean>;
	budget: {
		id: string;
		display_unit: string;
		exchange_rate: number;
		format: {
			singular: string;
			plural: string;
		};
		has_custom_currency: true;
		iso_code: string;
		plural: string;
		singular: string;
	};
	catalog_state: CatalogState;
	chat_integrations: ChatIntegrations;
	check_in_settings?: {
		mandatory: boolean;
		effective_date?: string;
	};
	company_hashtags: string[];
	core_values: Array<{ hashtag: string; description: string }>;
	created_at: string;
	currency_options: Array<{
		iso_code: string;
		name: string;
		symbol: string;
	}>;
	custom_theme?: {
		id: string;
		brand: string;
		theme_color: string | null;
	};
	custom_user_properties: string[];
	customize_give_amounts: boolean;
	disable_avatar_uploading: boolean;
	disable_gifs: boolean;
	disable_image_uploads: boolean;
	disable_video_recordings: boolean;
	disable_video_uploads: boolean;
	bizy_connectors_granola: boolean;
	bizy_connectors_linear: boolean;
	bizy_connectors_github: boolean;
	bizy_connectors_slack: boolean;
	email_customizations: Record<string, Record<string, string>>;
	email_default_texts: Record<string, Record<string, string>>;
	example_reason: string;
	free_tier_user_limit?: number;
	give_amounts: number[];
	harvest_anniversary_dates: boolean;
	has_active_benefactors: boolean;
	has_celebrations: boolean;
	has_claimable_awards: boolean;
	has_sample_data: boolean;
	hashtagging_mode: string;
	hide_competitive_features: boolean;
	hide_team_requests: boolean;
	/**
	 * Whether this is the Bonusly company.
	 */
	is_bonusly_company?: boolean;
	is_demo: boolean;
	is_superadmin_initialized: boolean;
	join_url: string;
	/**
	 * Limited release mode for One-on-Ones features. When true, allows specific users
	 * to pilot One-on-Ones features via user-level flags while keeping it disabled
	 * company-wide. Set to false when ready to roll out One-on-Ones to the entire organization.
	 */
	limited_release_mode: boolean;
	locale: string;
	login_methods?: {
		web_login_enabled: boolean;
		google_oauth_enabled: boolean;
		microsoft_oauth_enabled: boolean;
		saml_enabled: boolean;
	};
	logo_url: string;
	monarch_cohort: string;
	multi_factor_auth_enforced_at: string | null;
	multi_factor_auth_required: boolean;
	name: string;
	next_billing_at: string;
	product_plan_name: string;
	product_tier: ProductTier;
	promoted_rewards_setting: PromotedRewardsSetting;
	reward_catalog_auto_add_items: boolean;
	/**
	 * Whether post wrap (gift wrap collectibles) is enabled for this company and the current user.
	 * Computed server-side from the company's collectibles_enabled setting.
	 */
	post_wrap_enabled: boolean;
	/**
	 * Whether rewards billing is enabled for this company.
	 * When false, users can only redeem no-cost rewards (Points Boost, Exclusives, Swag).
	 * When true or undefined (for backwards compatibility), all rewards are available.
	 */
	rewards_billing_enabled?: boolean;
	rewards_billing_plan?: string;
	/**
	 * Whether the credit-card top-off feature is available to this company's users.
	 * Computed server-side from rewards billing being enabled, the admin having turned
	 * top-off on in catalog settings, and the super-admin kill switch being off.
	 */
	user_payment_topoff_enabled: boolean;
	send_activity_digest_emails: boolean;
	send_bonus_emails: boolean;
	send_manager_digest_emails: boolean;
	send_pending_reward_emails: boolean;
	send_reminder_emails: boolean;
	send_welcome_emails: boolean;
	settings_integrations_url: string;
	show_bonus_amounts: boolean;
	sign_up: CompanySignUp;
	sign_up_company_size: number;
	single_sign_on_enabled: boolean;
	status: string;
	subscription_days_past_due: number;
	tags: string[];
	time_zone: string;
	trial_end_date: string;
	trial_start_date: string;
	updated_at: string;
	user_earnings_cap: number | null;
	user_management_integration_url: string;
	users_externally_synced: boolean;
	workday_anytime_feedback_integration_id: string | null;
	workday_anytime_feedback_enabled: boolean;
	zero_point_bonuses: boolean;
}
